import React, {useCallback, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {findProductsByQueryString} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import GenericAsyncSelect from "../common/AsyncSelect";
import ProductOptionComponent from "./ProductOptionComponent";
import ProductAvailability from "./ProductAvailability";

/**
 * ProductSelectAsync Component
 *
 * An asynchronous product selection component with search functionality and availability checking.
 */
const ProductSelectAsync = (props) => {
    const {
        onItemClick,
        onClose,
        placeholder = "Selecione um produto...",
        value = null,
        onRequestRentProduct,
        prefilledProduct = null,
        style = {},
        index = null,
        dataTest = "product-async-select",
        maintainInputValue = true,
        onInputValueChange = null,
        showProductAvailability = false,
    } = props;
    const [, setLoading] = useRecoilState(loadingAtom);
    const [productAvailability, setProductAvailability] = useState(undefined);
    const selectRef = useRef(null);

    const handleAvailabilityClick = useCallback((product) => {
        selectRef.current?.blur();
        setProductAvailability(product);
    }, []);

    const loadOptions = useCallback(async (inputValue) => {
        if (!inputValue || inputValue.trim().length < 3) {
            return [];
        }

        try {
            setLoading(true);
            const response = await findProductsByQueryString(inputValue.trim());

            return response.map((product) => {
                const code = product.code && product.code > 0 ? product.code : '';
                const label = code ? `${code} - ${product.name}` : product.name;

                return {
                    showProductAvailability,
                    value: product.id,
                    label,
                    product,
                    onAvailabilityClick: handleAvailabilityClick,
                };
            });
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, [showProductAvailability, handleAvailabilityClick, setLoading]);

    const closeAvailability = () => {
        setProductAvailability(undefined);
    };

    const handleRequestRentProduct = (productId, date, days, quantity) => {
        if (onRequestRentProduct) {
            onRequestRentProduct(productId, date, days, quantity, index);
            setProductAvailability(undefined);
        }
    };

    const getPrefilledValue = () => {
        if (prefilledProduct) {
            const code = prefilledProduct.code && prefilledProduct.code > 0 ? prefilledProduct.code : '';
            const label = code ? `${code} - ${prefilledProduct.name}` : prefilledProduct.name;

            return {
                showProductAvailability,
                value: prefilledProduct.id,
                label,
                product: prefilledProduct,
                onAvailabilityClick: handleAvailabilityClick,
            };
        }
        return value;
    };

    return (
        <>
            <div className="product-select-async-row" style={{width: style.width || '100%'}}>
                <GenericAsyncSelect
                    ref={selectRef}
                    loadOptions={loadOptions}
                    onItemClick={onItemClick}
                    onClose={onClose}
                    placeholder={placeholder}
                    value={getPrefilledValue()}
                    dataTest={dataTest}
                    customOptionComponent={ProductOptionComponent}
                    style={{...style, width: '100%'}}
                    maintainInputValue={maintainInputValue}
                    onInputValueChange={onInputValueChange}
                    cacheUniqs={[showProductAvailability]}
                />
            </div>
            {productAvailability && createPortal(
                <ProductAvailability
                    onClose={closeAvailability}
                    product={productAvailability}
                    onRequestRentProduct={handleRequestRentProduct}
                />,
                document.body,
            )}
        </>
    );
};

export default ProductSelectAsync;
